import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBreadcrumbStore } from '@/store/breadcrumbs';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';

const LEFT_OVERFLOW_CONTROLS_WIDTH = 64;
const RIGHT_OVERFLOW_CONTROLS_WIDTH = 64;
const BREADCRUMB_PADDING_WIDTH = 32;
const BREADCRUMB_CHARACTER_WIDTH = 8;
const CHEVRON_WIDTH = 16;

interface BreadcrumbWindow {
    startIndex: number;
    endIndex: number;
    hasHiddenPrefix: boolean;
    hasHiddenSuffix: boolean;
}

function estimateBreadcrumbWidth(title: string, includeChevron: boolean): number {
    return (
        title.length * BREADCRUMB_CHARACTER_WIDTH +
        BREADCRUMB_PADDING_WIDTH +
        (includeChevron ? CHEVRON_WIDTH : 0)
    );
}

function computeBreadcrumbWindow(
    titles: readonly string[],
    startIndex: number,
    containerWidth: number
): BreadcrumbWindow {
    const clampedStartIndex: number = Math.min(Math.max(startIndex, 0), Math.max(0, titles.length - 1));
    let endIndex = clampedStartIndex;

    for (; endIndex < titles.length; endIndex += 1) {
        const nextEndIndex = endIndex;
        const hasHiddenPrefix: boolean = clampedStartIndex > 0;
        const hasHiddenSuffix: boolean = nextEndIndex < titles.length - 1;
        const controlsWidth: number =
            (hasHiddenPrefix ? LEFT_OVERFLOW_CONTROLS_WIDTH : 0) +
            (hasHiddenSuffix ? RIGHT_OVERFLOW_CONTROLS_WIDTH : 0);
        const breadcrumbWidth: number = titles
            .slice(clampedStartIndex, nextEndIndex + 1)
            .reduce((totalWidth, title, relativeIndex) => {
                return (
                    totalWidth +
                    estimateBreadcrumbWidth(
                        title,
                        clampedStartIndex + relativeIndex > 0
                    )
                );
            }, 0);

        if (controlsWidth + breadcrumbWidth > containerWidth) {
            endIndex = Math.max(clampedStartIndex, nextEndIndex - 1);
            break;
        }
    }

    if (endIndex >= titles.length) {
        endIndex = titles.length - 1;
    }

    return {
        startIndex: clampedStartIndex,
        endIndex,
        hasHiddenPrefix: clampedStartIndex > 0,
        hasHiddenSuffix: endIndex < titles.length - 1,
    };
}

export function BreadcrumbBar() {
    const items = useBreadcrumbStore((state) => state.items);
    const activeUrl = useBreadcrumbStore((state) => state.activeUrl);
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const containerRef = useRef<HTMLElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(1024);
    const [windowStartIndex, setWindowStartIndex] = useState<number>(0);
    const activeIndex: number = items.findIndex((item) => item.url === activeUrl);

    useEffect(() => {
        const element = containerRef.current;
        if (element === null) {
            return;
        }
        const resizeObserverConstructor = globalThis.ResizeObserver;
        if (resizeObserverConstructor === undefined) {
            return;
        }

        const resizeObserver = new resizeObserverConstructor((entries) => {
            const width = entries[0]?.contentRect.width;
            if (width !== undefined && width > 0) {
                setContainerWidth(width);
            }
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const breadcrumbWindow: BreadcrumbWindow = useMemo(() => {
        return computeBreadcrumbWindow(
            items.map((item) => item.title),
            windowStartIndex,
            containerWidth
        );
    }, [containerWidth, items, windowStartIndex]);

    useEffect(() => {
        if (items.length === 0 || activeIndex < 0) {
            setWindowStartIndex(0);
            return;
        }

        if (activeIndex < breadcrumbWindow.startIndex || activeIndex > breadcrumbWindow.endIndex) {
            setWindowStartIndex(activeIndex);
        }
    }, [activeIndex, breadcrumbWindow.endIndex, breadcrumbWindow.startIndex, items.length]);

    const visibleItems = items.slice(
        breadcrumbWindow.startIndex,
        breadcrumbWindow.endIndex + 1
    );

    return (
        <nav
            aria-label="Breadcrumbs"
            className="sticky top-0 z-20 shrink-0 overflow-hidden border-b bg-gray-50 px-8 py-4"
            ref={containerRef}
        >
            <ol className="m-0 flex list-none items-center gap-2 overflow-hidden p-0 text-sm leading-5 text-gray-600">
                {breadcrumbWindow.hasHiddenPrefix ? (
                    <li className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            aria-label="Scroll breadcrumbs left"
                            className="text-gray-600 hover:text-gray-900"
                            onClick={() => {
                                setWindowStartIndex((currentStartIndex) =>
                                    Math.max(0, currentStartIndex - 1)
                                );
                            }}
                        >
                            ◀
                        </button>
                        <span aria-hidden="true" className="text-gray-400">
                            …
                        </span>
                    </li>
                ) : null}
                {visibleItems.map((item, index) => {
                    const absoluteIndex = breadcrumbWindow.startIndex + index;
                    const isActive: boolean = item.url === activeUrl;

                    return (
                        <li key={item.url} className="flex min-w-0 items-center gap-2">
                            {absoluteIndex > 0 ? (
                                <span aria-hidden="true" className="text-gray-400">
                                    ›
                                </span>
                            ) : null}
                            <Link
                                to={item.url}
                                className={`truncate ${isActive ? 'font-semibold text-gray-900' : 'hover:underline'}`}
                                aria-current={isActive ? 'page' : undefined}
                                onClick={(event) => {
                                    event.preventDefault();
                                    breadcrumbNavigate({
                                        source: 'panel',
                                        target: {
                                            url: item.url,
                                            title: item.title,
                                        },
                                    });
                                }}
                            >
                                {item.title}
                            </Link>
                        </li>
                    );
                })}
                {breadcrumbWindow.hasHiddenSuffix ? (
                    <li className="flex items-center gap-2 shrink-0">
                        <span aria-hidden="true" className="text-gray-400">
                            …
                        </span>
                        <button
                            type="button"
                            aria-label="Scroll breadcrumbs right"
                            className="text-gray-600 hover:text-gray-900"
                            onClick={() => {
                                setWindowStartIndex((currentStartIndex) =>
                                    Math.min(items.length - 1, currentStartIndex + 1)
                                );
                            }}
                        >
                            ▶
                        </button>
                    </li>
                ) : null}
            </ol>
        </nav>
    );
}
